import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import styles from "./ConsentTemplatesPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import { FiEdit2 } from "react-icons/fi";
import { toast } from "react-toastify";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info" },
  { key: "logo", label: "Logo Management" },
  { key: "social", label: "Social Media Links" },
  { key: "portfolio", label: "Portfolio Gallery" },
  { key: "consent", label: "Consent Templates" },
  { key: "preview", label: "Preview & Complete" },
];

type Template = {
  id: number;
  name: string;
  body: string;
  level?: string;
  created_at?: string;
};

type Treatment = {
  id: number;
  category_type: string;
  category_name: string;
};

declare global {
  interface Window {
    CKEDITOR: any;
  }
}

export default function ConsentTemplatesPage() {
  const navigate = useNavigate();
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const [mode, setMode] = useState<"empty" | "builder">("empty");
  const [tab, setTab] = useState<"existing" | "generate">("existing");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [practiceTemplates, setPracticeTemplates] = useState<Template[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Template | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [user, setUser] = useState<any>(null);
  const [selectedLevel, setSelectedLevel] = useState<"user" | "practice">("user");
  const [selectedTreatments, setSelectedTreatments] = useState<number[]>([]);

  /* -------------------- Upload inline images -------------------- */
  const uploadCallback = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post("/uploads/image", formData);
    return res;
  };

  /* -------------------- Fetch user -------------------- */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) setUser(data[1]);
        else setUser(data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  /* -------------------- Fetch treatments -------------------- */
  useEffect(() => {
  const fetchTreatments = async () => {
    try {
      const token =
        axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
        localStorage.getItem("token") ||
        Cookies.get("token");

      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}treatment`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // ✅ Fix here: use res.data.treatments
      setTreatments(res.data?.treatments || []);
    } catch (err) {
      console.error("Failed to load treatments:", err);
      setTreatments([]); // safe fallback
    }
  };
  fetchTreatments();
}, []);

  /* -------------------- Fetch templates -------------------- */
  useEffect(() => {
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token =
        axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
        localStorage.getItem("token") ||
        Cookies.get("token");

      const endpoint =
        user?.level === "practice"
          ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_consent_templates`
          : `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_consent_templates`;

      const res = await axios.get(endpoint, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setPracticeTemplates(res.data || []);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  };

  // Run only when user is loaded
  if (user?.level) {
    fetchTemplates();
  }
}, [user?.level]);

  useEffect(() => {
    const fetchGlobalTemplates = async () => {
      if (tab !== "existing" || mode !== "builder") return;
      try {
        setLoading(true);
        const res = await axios.get(
         `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_global_consent_templates`,
          { headers: { Accept: "application/json" } }
        );
        setTemplates(res.data || []);
      } catch (err) {
        console.error("Failed to load global templates:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGlobalTemplates();
  }, [tab, mode]);

  /* -------------------- CKEditor setup -------------------- */
useEffect(() => {
  if (!editTemplate) return;

  // Load CKEditor script only once
  const loadEditor = () => {
    if (!editorRef.current) return;

    const existing = window.CKEDITOR.instances;
    Object.keys(existing || {}).forEach((key) => {
      existing[key].destroy(true);
    });

    const editor = window.CKEDITOR.replace(editorRef.current, {
      height: "450px",
    });

    editor.on("instanceReady", () => {
      editor.setData(editTemplate.body || "");
    });
  };

  if (!window.CKEDITOR) {
    const script = document.createElement("script");
    script.src = "/ckeditor/ckeditor.js";
    script.onload = loadEditor;
    document.body.appendChild(script);
  } else {
    loadEditor();
  }

  // Cleanup when modal closes
  return () => {
    const existing = window.CKEDITOR.instances;
    Object.keys(existing || {}).forEach((key) => {
      existing[key].destroy(true);
    });
  };
}, [editTemplate?.id]);

  /* -------------------- Save Template -------------------- */
  const handleSave = async () => {
    if (!editTemplate) return;
    if (selectedTreatments.length === 0) {
      toast.error("Please select at least one treatment.");
      return;
    }

    try {
      const editorContent = window.CKEDITOR.instances.editor1.getData();
      let updatedContent = editorContent;

      // Upload inline images if any
      const base64Regex = /<img[^>]+src="data:image\/[^;]+;base64,[^"]+"[^>]*>/g;
      const base64Images = editorContent.match(base64Regex);
      if (base64Images) {
        for (const img of base64Images) {
          const base64Data = img.match(/src="(data:image\/[^;]+;base64,[^"]+)"/)?.[1];
          if (base64Data) {
            const blob = await fetch(base64Data).then((res) => res.blob());
            const file = new File([blob], "upload.png", { type: blob.type });
            const uploadRes = await uploadCallback(file);
            updatedContent = updatedContent.replace(base64Data, uploadRes.data);
          }
        }
      }

      const payload = {
        body: updatedContent,
        is_cloned: false,
        is_default: false,
        level: selectedLevel,
        name: editTemplate.name,
        parent_id: null,
        treatment_id: selectedTreatments[0],
        treatment_ids: selectedTreatments,
      };

      const url = `${process.env.REACT_APP_BASE_URL}consent_clause/${editTemplate.id}/copy_global_template_to_private`;
      await axios.put(url, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success("Template saved successfully!");
      setEditTemplate(null);
      setMode("empty");
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to save template");
    }
  };

  const handleNext = () => navigate("/previewComplete");
  const handleBack = () => navigate("/portfolioGallery");

  /* -------------------- Render -------------------- */
  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.main}>
        <SetupSteps steps={STEPS} current={5} rightLabel="80% Complete" />

        {mode === "empty" ? (
          <>
            <h1 className={styles.title}>Consent Templates</h1>
            <p className={styles.subtitle}>
              Create consent templates for your most common procedures.
            </p>

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>Your Templates ({practiceTemplates.length})</h2>
                <button
                  className={styles.createBtnSmall}
                  onClick={() => setMode("builder")}
                >
                  + Create New Template
                </button>
              </div>

              {loading ? (
                <div className={styles.loading}>Loading...</div>
              ) : (
                <div className={styles.practiceTemplatesList}>
                  {practiceTemplates.map((t) => (
                    <div key={t.id} className={styles.practiceTemplateItem}>
                      <div>{t.name}</div>
                     <button
                      className={styles.viewBtn}
                      onClick={() => setPreview(t)}
                    >
                      View
                    </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          <>
            <div className={styles.builderHeader}>
              <h1>Create Consent Template</h1>
              <button
                className={styles.backToListBtn}
                onClick={() => {
                  setMode("empty");
                  setTab("existing");
                }}
              >
                ← Back
              </button>
            </div>

            <section className={styles.card}>
              <div className={styles.templatesGrid}>
                {templates.map((t) => (
                  <div key={t.id} className={styles.templateItem}>
                    <div>{t.name}</div>
                    <FiEdit2
                      className={styles.editIcon}
                      onClick={() => {
                        setEditTemplate(t);
                        setSelectedTreatments([]);
                        setSelectedLevel("user");
                      }}
                    />
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <div className={styles.nextWrapper}>
          <button className={styles.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <button className={styles.nextBtn} onClick={handleNext}>
            Next →
          </button>
        </div>
      </main>

      {/* ---------- Edit Modal ---------- */}
      {editTemplate && (
        <div className={styles.modalOverlay} onClick={() => setEditTemplate(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Edit Template</h2>
              <button className={styles.closeBtn} onClick={() => setEditTemplate(null)}>
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={editTemplate.name}
                  onChange={(e) =>
                    setEditTemplate({ ...editTemplate, name: e.target.value })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label>Select Treatments</label>
                <div className={styles.multiSelectBox}>
                  <div className={styles.selectedChips}>
                    {selectedTreatments.map((id) => {
                      const treatment = treatments.find((t) => t.id === id);
                      return (
                        <div key={id} className={styles.chip}>
                          {treatment?.category_name || `Treatment ${id}`}
                          <span
                            className={styles.removeChip}
                            onClick={() =>
                              setSelectedTreatments((prev) => prev.filter((x) => x !== id))
                            }
                          >
                            ✕
                          </span>
                        </div>
                      );
                    })}
                    <select
                      className={styles.treatmentSelect}
                      value=""
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value && !selectedTreatments.includes(value)) {
                          setSelectedTreatments((prev) => [...prev, value]);
                        }
                      }}
                    >
                      <option value="">Select Treatments</option>
                    {treatments.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.description}
                </option>
              ))}
                    </select>
                  </div>
                </div>
              </div>

             {/* Automatically set level based on logged-in user */}
            {(() => {
              if (user?.level === "practice" && selectedLevel !== "practice") {
                setSelectedLevel("practice");
              } else if (user?.level === "user" && selectedLevel !== "user") {
                setSelectedLevel("user");
              }
              return null; 
            })()}


              <textarea
                id="editor1"
                ref={editorRef}
                className={styles.editorTextarea}
                defaultValue={editTemplate.body}
              />
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.saveBtn} onClick={handleSave}>
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}


      
      {preview && (
  <div className={styles.modalOverlay} onClick={() => setPreview(null)}>
    <div
      className={`${styles.modalContent} ${styles.previewModal}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.modalHeader}>
        <h2>Preview: {preview.name}</h2>
        <button className={styles.closeBtn} onClick={() => setPreview(null)}>
          ✕
        </button>
      </div>

      <div className={styles.modalBody}>
        <div
          className={styles.previewBody}
          dangerouslySetInnerHTML={{ __html: preview.body }}
        />
      </div>

      <div className={styles.modalFooter}>
        <button
          className={styles.closeBtnOutlined}
          onClick={() => setPreview(null)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
    </div>


  );
}
